/** Port of TourDetailsScreenStateMapper.kt — the bottom-sheet list and the day tabs.
 *
 * Scope: the document half. Markers, KML and the floating activity carousel's coupling
 * to the map are NOT ported — the map layer is a separate phase, gated on a spike. The
 * in-list alternatives carousel IS here, since it is part of the list.
 *
 * Upstream: ui/my_trips_details/details/TourDetailsScreenStateMapper.kt
 * Spec: aidocs/context/android-renderer-spec.md §1A
 */
import {
  getDatesRange,
  slotDisplayIndex,
  TIMELINE_DISPLAY,
  type TourDay,
  type TourDomain,
  type TourTimeSlot,
} from '../adapters/tour';

export const STRINGS = {
  showMore: 'Show more',
  showLess: 'Show less',
} as const;

export type TimelinePosition = 'SINGLE' | 'TOP' | 'MIDDLE' | 'BOTTOM';
export type TimelineStyle = 'REGULAR' | 'EXTENDED';
export type TimelineSize = 'REGULAR' | 'SMALL';

export interface TourTab {
  id: number;
  label: string;
}

export type TourItem =
  | {
      kind: 'myTripHeader';
      key: string;
      title: string | null;
      subTitle: string | null;
      descriptionHtml: string | null;
    }
  | {
      kind: 'timelineDay';
      key: string;
      text: string;
      /** null when the slot has no coordinate-bearing activity — no numbered circle. */
      symbolNumber: number | null;
      position: TimelinePosition;
      style: TimelineStyle;
      size: TimelineSize;
    }
  | {
      kind: 'textBasedActivity';
      key: string;
      html: string;
      buttonText: string;
      expanded: boolean;
      lineVisible: boolean;
      slotId: number;
    }
  | {
      kind: 'alternatives';
      key: string;
      showLine: boolean;
      cards: {
        key: string;
        title: string;
        descriptionText: string | null;
        image: { prefix: string; suffix: string } | null;
        selected: boolean;
      }[];
    }
  | { kind: 'transport'; key: string; text: string; iconName: string | null };

export interface TourUiState {
  title: string;
  tabs: TourTab[];
  currentDayIndex: number;
  items: TourItem[];
  notes: string[];
}

/** `mapTimeline` — a server-driven display switch, not a client choice.
 *  HIDE yields Text.EMPTY, which is VISIBLE AND BLANK, not absent. */
function mapTimeline(slot: TourTimeSlot): string {
  switch (slot.timelineDisplay) {
    case TIMELINE_DISPLAY.TIMELINE_TEXT:
      return slot.timelineText ?? '';
    case TIMELINE_DISPLAY.TIMESLOT_TIME:
      return slot.time ? formatShortTime(slot.time) : '';
    case TIMELINE_DISPLAY.HIDE:
      return '';
    default:
      return slot.timelineText ?? '';
  }
}

/** Format.ShortTime — "H:mm" or "h:mm a" by locale. The wire carries "HH:MM:SS". */
export function formatShortTime(time: string): string {
  const [h = '0', m = '00'] = time.split(':');
  const hour = Number(h);
  const suffix = hour < 12 ? 'AM' : 'PM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${m} ${suffix}`;
}

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Format.ShortDay — "Mon 3/14". Tabs uppercase it. */
export function formatShortDay(d: Date): string {
  return `${DAY_ABBR[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`;
}

/** Format.LongDay — "Monday 3/14", used when the tour is a single day. */
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export function formatLongDay(d: Date): string {
  return `${DAY_FULL[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`;
}

/** Text.DASH — en-dash with spaces. */
const DASH = ' – ';

export function mapTabs(tour: TourDomain, now: Date): TourTab[] {
  const days = tour.days;
  if (days.length === 0) return [];

  if (days.length === 1) {
    const [start, end] = getDatesRange(days, 0, now);
    const label = end === null
      ? formatLongDay(start)
      : `${formatShortDay(start)}${DASH}${formatShortDay(end)}`;
    return [{ id: 0, label: label.toUpperCase() }];
  }

  return days.map((_, index) => {
    const [start, end] = getDatesRange(days, index, now);
    const label = end === null
      ? formatShortDay(start)
      : `${formatShortDay(start)}${DASH}${formatShortDay(end)}`;
    return { id: index, label: label.toUpperCase() };
  });
}

function positionFor(index: number, count: number): TimelinePosition {
  if (count === 1) return 'SINGLE';
  if (index === 0) return 'TOP';
  if (index === count - 1) return 'BOTTOM';
  return 'MIDDLE';
}

function mapTourDay(day: TourDay, expandedSlots: Set<number>): TourItem[] {
  const items: TourItem[] = [];

  // 1 — header, if ANY of title/subtitle/description is non-empty. Each sub-field is
  // then independently hidden, so a day with only a description collapses to that.
  if (day.title || day.subtitle || day.description) {
    items.push({
      kind: 'myTripHeader',
      key: 'header',
      title: day.title,
      subTitle: day.subtitle,
      descriptionHtml: day.description,
    });
  }

  const slots = day.timeslots;
  slots.forEach((slot, slotIndex) => {
    const lastSlot = slotIndex === slots.length - 1;

    // 2 — the rail, always, once per slot
    items.push({
      kind: 'timelineDay',
      key: `timeline_${slotIndex}`,
      text: mapTimeline(slot),
      symbolNumber: (() => {
        const n = slotDisplayIndex(day, slot);
        return n === null ? null : n + 1;
      })(),
      position: positionFor(slotIndex, slots.length),
      style: slotIndex === 0 ? 'REGULAR' : 'EXTENDED',
      size: slot.hasActivitiesWithCoordinates ? 'REGULAR' : 'SMALL',
    });

    // 3 — text-only slot body, collapsible at 4 lines
    if (slot.description) {
      const expanded = expandedSlots.has(slot.id);
      items.push({
        kind: 'textBasedActivity',
        key: `text_based_${slotIndex}`,
        html: slot.description,
        buttonText: expanded ? STRINGS.showLess : STRINGS.showMore,
        expanded,
        lineVisible: !lastSlot,
        slotId: slot.id,
      });
    }

    // 4 — the alternatives carousel
    if (slot.activities.length > 0) {
      items.push({
        kind: 'alternatives',
        key: `alternatives_${slotIndex}`,
        showLine: !lastSlot,
        cards: slot.activities.map((a) => ({
          key: `activity_${day.id}_${slot.id}_${a.id}`,
          title: a.name,
          // previewText wins over description, and the result is stripped to text.
          descriptionText: a.previewText ?? a.description,
          image: a.coverImage,
          selected: a.isSelected,
        })),
      });
    }

    // 5 — transport to the next slot
    if (slot.showTransport && !lastSlot && (slot.nextTransportName || slot.transportText)) {
      items.push({
        kind: 'transport',
        key: `next_transport_${slotIndex}`,
        text: slot.transportText ?? '',
        iconName: slot.nextTransportName,
      });
    }
  });

  return items;
}

export interface MapTourOptions {
  /** Which day tab is selected. */
  currentDayIndex?: number;
  /** Slot ids whose "Show more" is open. */
  expandedSlots?: Set<number>;
  /** Pinned, because template mode freezes the start to today at local midnight. */
  now?: Date;
}

export function mapTourDetails(tour: TourDomain, opts: MapTourOptions = {}): TourUiState {
  const now = opts.now ?? new Date();
  const days = [...tour.days].sort((a, b) => a.sequence - b.sequence);
  const tabs = mapTabs({ ...tour, days }, now);

  // In TEMPLATE mode the start is today, so getCurrentDayNumber always lands on day 1 —
  // unlike the booked-tour path, where it is wall-clock dependent.
  const index = Math.min(Math.max(opts.currentDayIndex ?? 0, 0), Math.max(days.length - 1, 0));
  const day = days[index];

  const notes = [
    'The map, its markers and KML layers are not part of this preview — only the ' +
      'bottom-sheet list and the day tabs.',
  ];

  return {
    title: tour.name,
    tabs,
    currentDayIndex: index,
    items: day ? mapTourDay(day, opts.expandedSlots ?? new Set()) : [],
    notes,
  };
}
